import { useId } from 'react'
import type { CSSProperties } from 'react'
import { getMotif } from './motifs'
import { shade, tint } from '../../lib/inviteStyle'

/**
 * A wax seal drawn for one template: a blob of wax in the template's own
 * accent colour with the template's motif pressed into it as a monochrome
 * relief — a dinosaur in green wax, a rocket in navy, a rose in blush. It
 * replaces the six photographed seals for the data-driven templates (the
 * five original designs keep their photographs).
 *
 * Geometry lives in a 120×120 box; the emblem is the motif icon from
 * motifs.tsx scaled into the seal's well. The icon paints through the
 * `--inv-*` colour handles, so the relief is produced by handing those
 * handles wax tones: a dark copy offset down-right (the recess) under a light
 * copy (the raised face).
 */

export type SealTones = {
  wax: string
  waxLight: string
  waxDark: string
  rim: string
  relief: { light: string; lighter: string; lightest: string; dark: string }
}

/** The wax and relief tones for an accent colour — one rule for every seal. */
export const sealTones = (color: string): SealTones => {
  const wax = shade(color, 0.18)
  return {
    wax,
    waxLight: tint(color, 0.12),
    waxDark: shade(color, 0.42),
    rim: tint(color, 0.35),
    relief: {
      light: tint(wax, 0.5),
      lighter: tint(wax, 0.62),
      lightest: tint(wax, 0.75),
      dark: shade(wax, 0.45),
    },
  }
}

// The wax blob: a circle of radius ~50 around (60, 60) whose edge wanders a
// little, as pressed wax does. Ten anchor points on a Catmull-Rom spline,
// converted to cubic Béziers once at module load.
const BLOB_PATH = (() => {
  const cx = 60
  const cy = 60
  const radii = [50, 47, 52.5, 49, 53, 48, 51, 50.5, 46.5, 52]
  const pts = radii.map((r, i) => {
    const a = (i / radii.length) * Math.PI * 2 - Math.PI / 2
    return [cx + r * Math.cos(a), cy + r * Math.sin(a)]
  })
  const n = pts.length
  const f = (v: number) => v.toFixed(2)
  let d = `M${f(pts[0][0])} ${f(pts[0][1])}`
  for (let i = 0; i < n; i += 1) {
    const p0 = pts[(i - 1 + n) % n]
    const p1 = pts[i]
    const p2 = pts[(i + 1) % n]
    const p3 = pts[(i + 2) % n]
    const c1 = [p1[0] + (p2[0] - p0[0]) / 6, p1[1] + (p2[1] - p0[1]) / 6]
    const c2 = [p2[0] - (p3[0] - p1[0]) / 6, p2[1] - (p3[1] - p1[1]) / 6]
    d += ` C${f(c1[0])} ${f(c1[1])}, ${f(c2[0])} ${f(c2[1])}, ${f(p2[0])} ${f(p2[1])}`
  }
  return `${d} Z`
})()

export const WAX_SEAL_BLOB = BLOB_PATH

type Props = {
  motif: string
  /** The template's accent (`palette.a1`) — the wax takes this hue. */
  color: string
  size?: number | string
  className?: string
  /** Draw the soft shadow under the wax (off where CSS supplies one). */
  shadow?: boolean
  /** Unique id prefix for the gradient defs; defaults to React's useId. */
  idPrefix?: string
  title?: string
}

const WaxSeal = ({ motif, color, size = 84, className, shadow = true, idPrefix, title }: Props) => {
  const rid = useId()
  const id = (idPrefix || rid).replace(/[^a-zA-Z0-9_-]/g, '')
  const t = sealTones(color)
  const { Icon } = getMotif(motif)

  const reliefVars = (dark: boolean) =>
    ({
      '--inv-a1': dark ? t.relief.dark : t.relief.light,
      '--inv-a2': dark ? t.relief.dark : t.relief.lighter,
      '--inv-gold': dark ? t.relief.dark : t.relief.lightest,
      '--inv-ink': dark ? t.relief.dark : t.relief.dark,
      '--inv-paper': dark ? t.relief.dark : t.waxDark,
    }) as CSSProperties

  // The emblem: the 100-box icon scaled into the 80-wide well.
  const emblem = 'translate(60 60) scale(0.56) translate(-50 -50)'

  return (
    <svg
      viewBox="0 0 120 120"
      width={size}
      height={size}
      className={className}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      overflow="visible"
    >
      <defs>
        <radialGradient id={`${id}-wax`} cx="36%" cy="30%" r="76%">
          <stop offset="0" stopColor={t.waxLight} />
          <stop offset="0.5" stopColor={t.wax} />
          <stop offset="1" stopColor={t.waxDark} />
        </radialGradient>
        <radialGradient id={`${id}-well`} cx="50%" cy="50%" r="50%">
          <stop offset="0.72" stopColor={t.waxDark} stopOpacity="0.16" />
          <stop offset="1" stopColor={t.waxDark} stopOpacity="0.5" />
        </radialGradient>
        <filter id={`${id}-soft`} x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="2.4" />
        </filter>
      </defs>

      {shadow && (
        <ellipse cx="63" cy="68" rx="50" ry="47" fill="#1b1033" opacity="0.32" filter={`url(#${id}-soft)`} />
      )}

      {/* the wax */}
      <path d={BLOB_PATH} fill={`url(#${id}-wax)`} />
      <path d={BLOB_PATH} fill="none" stroke={t.rim} strokeWidth="1.1" opacity="0.5" />

      {/* the pressed well and its ridge */}
      <circle cx="60" cy="60" r="41" fill={`url(#${id}-well)`} />
      <circle cx="60" cy="60" r="41" fill="none" stroke={t.waxDark} strokeWidth="2.2" opacity="0.35" />
      <circle cx="59.2" cy="59.2" r="41" fill="none" stroke={t.rim} strokeWidth="1.2" opacity="0.55" />

      {/* the emblem in relief: the recess, then the raised face */}
      <g transform={emblem} style={reliefVars(true)} opacity="0.85">
        <g transform="translate(1.6 1.9)">
          <Icon />
        </g>
      </g>
      <g transform={emblem} style={reliefVars(false)}>
        <Icon />
      </g>

      {/* gloss */}
      <ellipse cx="42" cy="34" rx="24" ry="13" fill="#ffffff" opacity="0.16" filter={`url(#${id}-soft)`} />
    </svg>
  )
}

export default WaxSeal
